//Feb25 URLFieldComponent working correctly but not optimized for handleBlur
//Feb25 URLFieldComponent with onBlur implemented:
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { InputAdornment, CircularProgress, FormControl } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CustomFieldWrapper from './CustomFieldWrapper';
import { useFieldEdit } from '../../contexts/FieldEditContext';

const URLFieldComponent = ({
  fieldId,
  fieldMetadata = {},
  value: propValue,
  onChange,
  disabled = false,
  isSubmitting = false,
  isBuilderMode = false,
  setShowDropZones,
  variant,
  error,
  helperText,
  externalHelperText,
  submitted = false,
  externalError = false,
}) => {
  const [internalValue, setInternalValue] = useState(propValue);
  const [validatedValue, setValidatedValue] = useState(propValue);
  const [internalError, setInternalError] = useState(false);
  const [internalErrorMessage, setInternalErrorMessage] = useState('');
const { updateValidationError } = useFieldEdit(); //  Only importing what we need

  const [loading, setLoading] = useState(false);

useEffect(() => {
    if (propValue !== internalValue) {
        setInternalValue(propValue);
        setValidatedValue(propValue); //  Ensure validatedValue stays updated too!
    }
}, [propValue]);

  // Memoize transformations to avoid unnecessary re-renders
  const transformations = useMemo(() => fieldMetadata.transformations || {}, [fieldMetadata.transformations]);

  const applyTransformations = useCallback((inputValue) => {
    const { capitalize, spacing, allowSymbols } = transformations;
    let transformedValue = inputValue;

    // Apply capitalization
    switch (capitalize) {
      case 'first-letter':
        transformedValue = transformedValue.charAt(0).toUpperCase() + transformedValue.slice(1).toLowerCase();
        break;
      case 'all-caps':
        transformedValue = transformedValue.toUpperCase();
        break;
      case 'no-caps':
        transformedValue = transformedValue.toLowerCase();
        break;
      default:
        break;
    }

    // Apply spacing transformations
    switch (spacing) {
      case 'underscore':
        transformedValue = transformedValue.replace(/\s+/g, '_');
        break;
      case 'hyphen':
        transformedValue = transformedValue.replace(/\s+/g, '-');
        break;
      case 'no-spacing':
        transformedValue = transformedValue.replace(/\s+/g, '');
        break;
      default:
        break;
    }

    if (!allowSymbols) {
      transformedValue = transformedValue.replace(/[^\w\s-]/gi, '');
    }

    return transformedValue;
  }, [transformations]);

  // URL validation logic
  const validateURL = useCallback((url) => {
    const urlRegex = /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})$/;
    return urlRegex.test(url);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // CSV support (enterprise-safe):
  // - Only treat input as “multi URL” when it contains a comma.
  // - Split, trim, drop empties, de-dupe (preserve order).
  // - Validation happens on submit AND here on blur for UX.
  // ─────────────────────────────────────────────────────────────
  const splitCsvUrls = useCallback((input) => {
    const raw = String(input ?? '');
    if (!raw.includes(',')) return null;

    const parts = raw
      .split(',')
      .map(s => String(s ?? '').trim())
      .filter(Boolean);

    const seen = new Set();
    const out = [];
    for (const p of parts) {
      if (!seen.has(p)) { seen.add(p); out.push(p); }
    }
    return out.length ? out : null;
  }, []);

const handleValidation = useCallback((inputValue) => {
  let newError = false;
  let newErrorMessage = '';

  const raw = String(inputValue ?? '').trim();

  if (!raw) {
    newError = false;
    newErrorMessage = '';
  } else {
    // CSV path: validate ALL urls
    const csv = splitCsvUrls(raw);
    if (csv && Array.isArray(csv) && csv.length > 0) {
      const invalid = csv.filter(u => !validateURL(u));
      if (invalid.length > 0) {
        newError = true;
        newErrorMessage =
          `One or more URLs are invalid. Please fix: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '…' : ''}`;
      } else {
        newError = false;
        newErrorMessage = '';
      }
    } else {
      // Single URL path (original behavior)
      if (!validateURL(raw)) {
        newError = true;
        newErrorMessage = 'Please enter a valid URL (starting with http:// or https://)';
      }
    }
  }

  setInternalError(newError);
  setInternalErrorMessage(newErrorMessage);

  //  Push to FieldEditContext (same behavior as before)
  updateValidationError(
    fieldId,
    newErrorMessage,
    newError ? 'Error! Ensure the URL is correct (starting with http:// or https://)' : null
  );

}, [validateURL, splitCsvUrls, fieldId, updateValidationError]);



const handleChange = useCallback((event) => {
  let inputValue = event.target.value;

  // 🛠️ Apply transformations (e.g., capitalization, spacing rules)
  inputValue = applyTransformations(inputValue);

  // 🛠️ Enforce max length restriction if defined in fieldMetadata
  if (fieldMetadata.maxLength && inputValue.length > fieldMetadata.maxLength) {
    inputValue = inputValue.substring(0, fieldMetadata.maxLength);
  }

  //  Update local state for real-time display, but do NOT trigger validation or external updates
  setInternalValue(inputValue);

//  console.log(`[URLFieldComponent: handleChange] User typed: "${inputValue}"`);

}, [applyTransformations, fieldMetadata.maxLength]);


const handleBlur = useCallback(() => {
  if (isBuilderMode) {
    setShowDropZones(true);
  }

//  console.log(`[URLFieldComponent: handleBlur] Validating input: "${internalValue}"`);

  //  Run validation onBlur
  handleValidation(internalValue);

  //  If valid, update the form context
  const raw = String(internalValue ?? '').trim();
  const csv = splitCsvUrls(raw);

  const csvIsValid =
    csv && Array.isArray(csv) && csv.length > 0
      ? csv.every(u => validateURL(u))
      : false;

  const singleIsValid = raw ? validateURL(raw) : true; // empty allowed

  if (csvIsValid || (!csv && singleIsValid)) {
    setValidatedValue(internalValue);
    onChange({ target: { value: internalValue } }); // 🔥 Updating form context ONLY onBlur
  } else {
    // keep original behavior: do not commit invalid input
//    console.log(`[URLFieldComponent: handleBlur] Input is invalid, form state NOT updated.`);
  }


}, [isBuilderMode, setShowDropZones, handleValidation, internalValue, onChange, validateURL]);



  useEffect(() => {
    if (isSubmitting) {
      const finalValue = transformations.encode ? encodeURIComponent(internalValue) : internalValue;
      onChange({ target: { value: finalValue } });
    }
  }, [isSubmitting, internalValue, onChange, fieldId, transformations.encode]);

  // End adornment for loading and status icons
  const endAdornment = useMemo(() => (
    <InputAdornment position="end">
      {loading && <CircularProgress size={20} />}
      {!loading && !internalError && !externalError && validatedValue && <CheckCircleOutlineIcon color="success" />}
      {!loading && (internalError || externalError) && <ErrorOutlineIcon color="error" />}
    </InputAdornment>
  ), [loading, internalError, externalError, validatedValue]);

const finalHelperText = submitted && fieldMetadata.required && !internalValue 
  ? 'This field is required'
  : internalError ? internalErrorMessage 
  : helperText;

  return (
    <FormControl fullWidth disabled={disabled || isSubmitting}>
      <CustomFieldWrapper
        fieldId={fieldId}  
        label={fieldMetadata.label || 'Field'}
        variant={variant}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        required={fieldMetadata.required}
        error={error || (submitted && fieldMetadata.required && !internalValue) || internalError}
        helperText={finalHelperText}
        externalHelperText={externalHelperText}
        placeholder={fieldMetadata.hint || 'Enter a valid URL'}
        fullWidth
        InputProps={{
          endAdornment,
          inputProps: {
            maxLength: fieldMetadata.maxLength,
          },
        }}
        disabled={disabled || isSubmitting}
      />
    </FormControl>
  );
};


URLFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
  fieldMetadata: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    externalError: PropTypes.bool,
    externalHelperText: PropTypes.string,
    helperText: PropTypes.string,
    hint: PropTypes.string,
    transformations: PropTypes.object,
  }),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isBuilderMode: PropTypes.bool,
  setShowDropZones: PropTypes.func,
  variant: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  externalHelperText: PropTypes.string,
  submitted: PropTypes.bool,
  externalError: PropTypes.bool,
};

export default URLFieldComponent;
